/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import { useSearchFormStore } from '../store/searchFormStore';
import { useMarriottAIPreviewStore } from '../store';
import { useSearchFilterStore } from '../modules/store/searchFilterZustandStore';
import { executeNLSSearchWithFilters, executeNLSFilterRefresh } from '../services/nlsSearchService';
import {
  NLSSearchByNaturalLanguageQueryResult,
  NLSLlmMetadata,
  NLSAppliedFilters,
  NLSSelectedFacetMap,
  NLSFacet,
} from '../services/nlsSearch.types';
import { uiContextManager } from '../utils/uiContextManager';
import { DATES, ROOMS_AND_GUESTS, USE_POINTS, BRANDS, SPECIAL_RATES } from '../store/store.constants';
import { SearchFacetsConstants } from '../organisms/OSearchResults/index.constant';

export interface UseNLSSearchOptions {
  /** Callback when search starts */
  onSearchStart?: () => void;
  /** Callback when search completes successfully */
  onSearchComplete?: (result: NLSSearchByNaturalLanguageQueryResult) => void;
  /** Callback when search fails */
  onSearchError?: (error: Error) => void;
  /** Additional headers for GraphQL requests */
  headers?: Record<string, string>;
  /** Default pagination limit */
  defaultLimit?: number;
}

export interface UseNLSSearchReturn {
  /** Execute NLS search with current form state */
  executeSearch: (
    baseQueryRewrite: string,
    appliedFilters?: NLSAppliedFilters,
    storeOverrides?: Partial<{ dates: any; roomsAndGuests: any; usePoints: any; specialRates: any }>,
    filterOptions?: { includeUnavailableProperties?: boolean }
  ) => Promise<NLSSearchByNaturalLanguageQueryResult | null>;
  /** Execute search when dates change */
  onDatesChange: (dates: {
    checkin?: string;
    checkout?: string;
    flexible?: string;
    numberOfNights?: number;
  }) => Promise<void>;
  /** Execute search when rooms/guests change */
  onRoomsChange: (rooms: {
    roomsCount: string;
    adultsCount: number;
    childrenCount?: number;
    childrenAges?: number[];
  }) => Promise<void>;
  /** Execute search when special rates change */
  onRatesChange: (rates: { clusterCode: string; corporateCode?: string; groupCode?: string }) => Promise<void>;
  /** Execute search when facet filters change */
  onFiltersChange: (filters: NLSAppliedFilters) => Promise<void>;
  /**
   * Called on every individual facet click — fires the Filter query (no edges)
   * to refresh bucket counts in the filter UI, without updating search results.
   * Mirrors shop's per-click filter refresh pattern.
   */
  onFacetClick: (updatedFacets: NLSSelectedFacetMap) => Promise<NLSFacet[] | null>;
  /**
   * Apply the currently selected facets — fires the full NLS search with
   * selectedFacets in the payload and updates search results.
   * @param selectedFacets - The selected facets to apply
   * @param options - Optional overrides for search parameters
   */
  onApplyFilters: (
    selectedFacets: NLSSelectedFacetMap,
    options?: { includeUnavailableProperties?: boolean }
  ) => Promise<void>;
  /** Load more results (pagination) */
  loadMore: () => Promise<void>;
  /** Current search results */
  searchResults: NLSSearchByNaturalLanguageQueryResult | null;
  /** Current LLM metadata (contains queryRewrite) */
  llmMetadata: NLSLlmMetadata | null;
  /** Loading state */
  isLoading: boolean;
  /** True while a facet-refresh (filter query) is in flight */
  isFacetRefreshing: boolean;
  /** Error state */
  error: Error | null;
  /** Current offset for pagination */
  currentOffset: number;
  /** Whether there are more results to load */
  hasMore: boolean;
  /**
   * Monotonically increasing counter, incremented after every successful executeSearch.
   * Can be used by callers for diagnostics/metrics around NLS refreshes.
   */
  searchVersion: number;
  /** Currently selected facet map — drives the filter UI active state */
  selectedFacets: NLSSelectedFacetMap;
  /** Refreshed facets from the last filter-query call — for updating UI counts */
  refreshedFacets: NLSFacet[] | null;
  /**
   * Seed the internal base-query ref without triggering a search.
   * Call this when the initial TIP result arrives (via llmMetadata) so that
   * onFacetClick / onApplyFilters work before the user has run a MiniSearchBar search.
   */
  setBaseQuery: (query: string) => void;
  /**
   * Seed the internal selectedFacetsRef without triggering a search.
   * Call this when switching conversations to restore the filter state,
   * so subsequent searches include the restored filters.
   */
  syncSelectedFacets: (facets: NLSSelectedFacetMap) => void;
}

function buildViewDataFromNLSResult(result: NLSSearchByNaturalLanguageQueryResult, isFlexDate?: boolean) {
  return {
    rawData: {
      data: {
        search: {
          conversationalSearch: {
            searchByNaturalLanguageQuery: result,
          },
        },
      },
    },
    totalResults: result.total,
    // Carry the effective flexible-date mode so the viewData→store sync effect
    // in OChat does not write back a stale isFlexDate over the user's selection.
    ...(isFlexDate !== undefined && { isFlexDate }),
  };
}

export function useNLSSearch(options: UseNLSSearchOptions = {}): UseNLSSearchReturn {
  const { onSearchStart, onSearchComplete, onSearchError, headers, defaultLimit = 40 } = options;

  const client = useApolloClient();

  // Store selectors
  const dates = useSearchFormStore((s: any) => s[DATES]);
  const roomsAndGuests = useSearchFormStore((s: any) => s[ROOMS_AND_GUESTS]);
  const usePoints = useSearchFormStore((s: any) => s[USE_POINTS]);
  const brands = useSearchFormStore((s: any) => s[BRANDS]);
  const specialRates = useSearchFormStore((s: any) => s[SPECIAL_RATES]);
  const setSearchFormState = useSearchFormStore((s: any) => s.setSearchFormState);

  // Preview store for updating results
  const updateViewData = useMarriottAIPreviewStore(s => s.updateViewData);
  const setIsProcessing = useMarriottAIPreviewStore(s => s.setIsProcessing);

  // Local state
  const [searchResults, setSearchResults] = useState<NLSSearchByNaturalLanguageQueryResult | null>(null);
  const [llmMetadata, setLlmMetadata] = useState<NLSLlmMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFacetRefreshing, setIsFacetRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedFacets, setSelectedFacets] = useState<NLSSelectedFacetMap>({});
  const [refreshedFacets, setRefreshedFacets] = useState<NLSFacet[] | null>(null);
  // Increments on every successful executeSearch so callers can use it as a React key
  // to force-remount components that initialize internal state from props on mount.
  const [searchVersion, setSearchVersion] = useState(0);

  // Refs for tracking state
  const appliedFiltersRef = useRef<NLSAppliedFilters>({});
  const baseQueryRef = useRef<string>('');
  const selectedFacetsRef = useRef<NLSSelectedFacetMap>({});

  const executeSearch = useCallback(
    async (
      baseQueryRewrite: string,
      appliedFilters?: NLSAppliedFilters,
      storeOverrides?: Partial<{ dates: any; roomsAndGuests: any; usePoints: any; specialRates: any }>,
      filterOptions?: { includeUnavailableProperties?: boolean }
    ): Promise<NLSSearchByNaturalLanguageQueryResult | null> => {
      try {
        setIsLoading(true);
        setError(null);
        onSearchStart?.();
        setIsProcessing(true);

        baseQueryRef.current = baseQueryRewrite;
        if (appliedFilters) {
          appliedFiltersRef.current = appliedFilters;
        }

        // Build search form state from store, applying any overrides passed by
        // onChange handlers to avoid stale-closure issues (store update is async).
        //
        // For usePoints: Check the search filter store's points flag first.
        // When user selects points in the filter modal, it updates searchQueryState[SearchFacetsConstants.points]
        // in the search filter store, not useSearchFormStore. This mirrors shop's behavior where
        // getSearchAppliedFilter reads currentState[SearchFacetsConstants.points] to determine
        // whether to send PRICE or POINTS range in the facets payload.
        const searchFilterPoints = useSearchFilterStore.getState().searchQueryState?.[SearchFacetsConstants.points];
        const effectiveUsePoints =
          storeOverrides?.usePoints ?? (searchFilterPoints === true ? { rewardsRedemption: true } : usePoints);

        const searchFormState = {
          dates: storeOverrides?.dates ?? dates,
          roomsAndGuests: storeOverrides?.roomsAndGuests ?? roomsAndGuests,
          usePoints: effectiveUsePoints,
          brands,
          specialRates: storeOverrides?.specialRates ?? specialRates,
        };

        // Determine includeUnavailableProperties:
        // 1. Use filterOptions override if provided (from filter modal apply)
        // 2. Otherwise read from the search filter store
        const includeUnavailableProperties =
          filterOptions?.includeUnavailableProperties ??
          useSearchFilterStore.getState().searchQueryState?.[SearchFacetsConstants.unavailableProperties] ??
          true;

        const result = await executeNLSSearchWithFilters(
          client,
          baseQueryRewrite,
          searchFormState,
          {
            amenities: appliedFiltersRef.current.facets
              ?.filter(f => f.code === 'amenities')
              .flatMap(f => f.values as string[]),
            priceRange: appliedFiltersRef.current.priceRange,
            selectedFacets: selectedFacetsRef.current,
            includeUnavailableProperties,
          },
          headers,
          { limit: defaultLimit, offset: 0 }
        );

        if (result) {
          setSearchResults(result);
          setLlmMetadata(result.llmMetadata || null);
          setCurrentOffset(0);
          setHasMore(result.pageInfo?.hasNextPage || false);
          // Keep a monotonic version counter for telemetry/debugging.
          setSearchVersion(v => v + 1);

          // Mark that user has explicitly modified search parameters.
          // This flag is checked when sending the next message to TIP.
          uiContextManager.markSearchModified();

          // Build displayed_results for ui_context - matches TIP's expected structure
          const displayedResults = (result.edges || []).map((edge: any, index: number) => {
            const node = edge?.node;
            const property = node?.property;
            const basicInfo = property?.basicInformation;
            return {
              position: index + 1,
              property_id: property?.id,
              property_name: basicInfo?.name,
            };
          });

          uiContextManager.setState('search_results_filtered', {
            total_results: result.total,
            query: baseQueryRewrite,
            filters_applied: Object.keys(appliedFiltersRef.current).length > 0,
            displayed_results: displayedResults,
          });

          // Update only view-data fields. Do not mutate viewType/navigation state.
          // Pass the effective flexible-date mode (from the user's current search
          // form / override) so the viewData→store sync effect honors the user's
          // manual flexible/fixed selection instead of a stale backend value.
          const effectiveIsFlexDate =
            searchFormState.dates?.flexible === 'true' || searchFormState.dates?.flexible === true;
          updateViewData(buildViewDataFromNLSResult(result, effectiveIsFlexDate));

          onSearchComplete?.(result);
        }

        return result;
      } catch (err) {
        const searchError = err instanceof Error ? err : new Error('Search failed');
        setError(searchError);
        onSearchError?.(searchError);
        return null;
      } finally {
        setIsLoading(false);
        setIsProcessing(false);
      }
    },
    [
      client,
      dates,
      roomsAndGuests,
      usePoints,
      brands,
      specialRates,
      headers,
      defaultLimit,
      onSearchStart,
      onSearchComplete,
      onSearchError,
      updateViewData,
      setIsProcessing,
    ]
  );

  const onDatesChange = useCallback(
    async (newDates: { checkin?: string; checkout?: string; flexible?: string; numberOfNights?: number }) => {
      const fromDate = newDates.checkin ? moment(newDates.checkin) : dates?.fromDate;
      const toDate = newDates.checkout ? moment(newDates.checkout) : dates?.toDate;

      const updatedDates = {
        ...dates,
        fromDate,
        toDate,
        flexible: newDates.flexible,
        numberOfNights: newDates.numberOfNights,
        checkInDate: newDates.checkin || '',
        checkOutDate: newDates.checkout || '',
      };

      setSearchFormState([DATES], { [DATES]: updatedDates });

      // Pass fresh dates as override to avoid stale closure
      if (baseQueryRef.current) {
        await executeSearch(baseQueryRef.current, undefined, { dates: updatedDates });
      }
    },
    [dates, setSearchFormState, executeSearch]
  );

  const onRoomsChange = useCallback(
    async (rooms: { roomsCount: string; adultsCount: number; childrenCount?: number; childrenAges?: number[] }) => {
      const updatedRooms = {
        ...roomsAndGuests,
        numRooms: rooms.roomsCount,
        numAdultsPerRoom: rooms.adultsCount,
        numChildrenPerRoom: rooms.childrenCount || 0,
        childrenAges: rooms.childrenAges || [],
      };

      setSearchFormState([ROOMS_AND_GUESTS], { [ROOMS_AND_GUESTS]: updatedRooms });

      // Pass fresh rooms as override to avoid stale closure
      if (baseQueryRef.current) {
        await executeSearch(baseQueryRef.current, undefined, { roomsAndGuests: updatedRooms });
      }
    },
    [roomsAndGuests, setSearchFormState, executeSearch]
  );

  const onRatesChange = useCallback(
    async (rates: { clusterCode: string; corporateCode?: string; groupCode?: string }) => {
      const updatedRates = {
        ...specialRates,
        clusterCode: rates.clusterCode,
        corporateCode: rates.corporateCode || '',
        groupCode: rates.groupCode || '',
      };

      setSearchFormState([SPECIAL_RATES], { [SPECIAL_RATES]: updatedRates });

      // Pass fresh rates as override to avoid stale closure
      if (baseQueryRef.current) {
        await executeSearch(baseQueryRef.current, undefined, { specialRates: updatedRates });
      }
    },
    [specialRates, setSearchFormState, executeSearch]
  );

  const onFiltersChange = useCallback(
    async (filters: NLSAppliedFilters) => {
      appliedFiltersRef.current = filters;
      if (baseQueryRef.current) {
        await executeSearch(baseQueryRef.current, filters);
      }
    },
    [executeSearch]
  );

  /**
   * Called on every individual facet dimension click inside the filter panel.
   * Fires the facets-only Filter query to refresh bucket counts.
   * The shop's own getUpdatedFilterOnUSelection handler (already wired to
   * updateShopPageFilterModalValues) sets filterUpdateData → FiltersComponent
   * re-renders with new counts. We do NOT touch viewData here — that would
   * change searchResultsKey and remount SearchResultsComponent, closing the modal.
   */
  const onFacetClick = useCallback(
    async (updatedFacets: NLSSelectedFacetMap): Promise<NLSFacet[] | null> => {
      selectedFacetsRef.current = updatedFacets;
      setSelectedFacets(updatedFacets);

      if (!baseQueryRef.current) return null;

      try {
        setIsFacetRefreshing(true);
        // Check search filter store's points flag - mirrors shop's behavior
        const searchFilterPoints = useSearchFilterStore.getState().searchQueryState?.[SearchFacetsConstants.points];
        const effectiveUsePoints = searchFilterPoints === true ? { rewardsRedemption: true } : usePoints;
        const searchFormState = { dates, roomsAndGuests, usePoints: effectiveUsePoints, brands, specialRates };
        const result = await executeNLSFilterRefresh(
          client,
          baseQueryRef.current,
          searchFormState,
          updatedFacets,
          headers
        );
        if (result?.facets) {
          setRefreshedFacets(result.facets);
          return result.facets;
        }
        return null;
      } catch (err) {
        console.error('[useNLSSearch] onFacetClick error:', err);
        return null;
      } finally {
        setIsFacetRefreshing(false);
      }
    },
    [client, dates, roomsAndGuests, usePoints, brands, specialRates, headers]
  );

  /**
   * Called when user clicks Apply in the filter panel.
   * Fires the full NLS search with the currently selected facets in the payload
   * and updates the search results.
   * @param facetsToApply - The selected facets to apply
   * @param options - Optional overrides for search parameters
   */
  const onApplyFilters = useCallback(
    async (facetsToApply: NLSSelectedFacetMap, options?: { includeUnavailableProperties?: boolean }): Promise<void> => {
      selectedFacetsRef.current = facetsToApply;
      setSelectedFacets(facetsToApply);
      if (baseQueryRef.current) {
        // If includeUnavailableProperties is provided, pass it as an override
        // Otherwise, executeSearch will read from the store
        await executeSearch(baseQueryRef.current, undefined, undefined, options);
      }
    },
    [executeSearch]
  );

  /**
   * Seed the internal baseQueryRef without triggering a search.
   * Used by HotelResultsView to prime the ref from llmMetadata after the
   * initial TIP response, so onFacetClick/onApplyFilters work before the
   * user has ever triggered a MiniSearchBar search.
   */
  const setBaseQuery = useCallback((query: string) => {
    if (query) {
      baseQueryRef.current = query;
    }
  }, []);

  /**
   * Seed the internal selectedFacetsRef without triggering a search.
   * Used by HotelResultsView to restore facets when switching conversations,
   * so subsequent searches include the restored filters.
   */
  const syncSelectedFacets = useCallback((facets: NLSSelectedFacetMap) => {
    selectedFacetsRef.current = facets;
    setSelectedFacets(facets);
  }, []);

  const loadMore = useCallback(async () => {
    if (!baseQueryRef.current || !hasMore || isLoading) return;

    try {
      setIsLoading(true);
      const newOffset = currentOffset + defaultLimit;

      // Check search filter store's points flag - mirrors shop's behavior
      const searchFilterPoints = useSearchFilterStore.getState().searchQueryState?.[SearchFacetsConstants.points];
      const effectiveUsePoints = searchFilterPoints === true ? { rewardsRedemption: true } : usePoints;

      const result = await executeNLSSearchWithFilters(
        client,
        baseQueryRef.current,
        { dates, roomsAndGuests, usePoints: effectiveUsePoints, brands, specialRates },
        {
          amenities: appliedFiltersRef.current.facets
            ?.filter(f => f.code === 'amenities')
            .flatMap(f => f.values as string[]),
          priceRange: appliedFiltersRef.current.priceRange,
          selectedFacets: selectedFacetsRef.current,
          includeUnavailableProperties:
            useSearchFilterStore.getState().searchQueryState?.[SearchFacetsConstants.unavailableProperties] ?? true,
        },
        headers,
        { limit: defaultLimit, offset: newOffset }
      );

      if (result) {
        setSearchResults(prev => {
          if (!prev) return result;
          return { ...result, edges: [...(prev.edges || []), ...(result.edges || [])] };
        });
        setCurrentOffset(newOffset);
        setHasMore(result.pageInfo?.hasNextPage || false);
      }
    } catch (err) {
      console.error('[useNLSSearch] Load more error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    client,
    dates,
    roomsAndGuests,
    usePoints,
    brands,
    specialRates,
    headers,
    defaultLimit,
    currentOffset,
    hasMore,
    isLoading,
  ]);

  return {
    executeSearch,
    onDatesChange,
    onRoomsChange,
    onRatesChange,
    onFiltersChange,
    onFacetClick,
    onApplyFilters,
    loadMore,
    searchResults,
    llmMetadata,
    isLoading,
    isFacetRefreshing,
    error,
    currentOffset,
    hasMore,
    searchVersion,
    selectedFacets,
    refreshedFacets,
    setBaseQuery,
    syncSelectedFacets,
  };
}

export default useNLSSearch;
